'use client'

import { useAbTest } from 'ab-test/hooks/useAbTest/useAbTest'
import type { CrossSellingServiceResponse } from 'cross-selling/types'
import { FloatingButton } from 'fukku/Button/Floating'
import { MODAL_TYPE, Modal, type ModalAPI } from 'fukku/Modal'
import { IconDone } from 'icons/components/IconDone'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import dynamic from 'next/dynamic'
import {
	Snackbar,
	type SnackbarHandler,
} from 'notifications/components/Snackbar/Snackbar'
import { SnackbarPosition } from 'notifications/constants/constants'
import { useRef } from 'react'
import { AbTestKeys } from 'types/constants/AbTestKeys'
import { cx } from 'utils/cx'

import { useAnalytics } from '../../contexts/AnalyticsProvider/AnalyticsProvider'
import type { CommonProps } from '../../types/CommonProps'

const NOTIFICATION_DELAY = 4000

const DynamicShopTheLookContent = dynamic(() =>
	import('./ShopTheLookContent').then((mod) => mod.ShopTheLookContent)
)
type ShopTheLookProps = Pick<CommonProps, 'look'> & {
	className?: string
	products: CrossSellingServiceResponse[]
}
export function ShopTheLook({
	className,
	look,
	products,
}: ShopTheLookProps): JSX.Element | null {
	const modalRef = useRef<ModalAPI>(null)
	const snackbarRef = useRef<SnackbarHandler | null>(null)

	const { sendShopTheLookOpenEvent } = useAnalytics()
	const { t } = useLabels()
	const {
		country: { hasOnlineSale },
	} = useMasterData()
	const abTest = useAbTest({
		key: AbTestKeys.pdp_shop_the_look,
		defaultDecision: { enabled: false },
	})

	const onShopTheLookClick = (): void => {
		modalRef.current?.show()
		sendShopTheLookOpenEvent()
	}

	const onAddToCart = () => snackbarRef.current?.open()

	const onClose = () => snackbarRef.current?.close()

	return abTest?.enabled && products.length > 0 ? (
		<>
			<FloatingButton className={cx(className)} onClick={onShopTheLookClick}>
				{t('pdp.getTheLook.button')}
			</FloatingButton>
			<Modal
				type={MODAL_TYPE.SHEET_FULL_SCREEN}
				name='pdp-shop-the-look'
				title={t('pdp.getTheLook.title')}
				showTitle
				ref={modalRef}
				onClose={onClose}
			>
				<DynamicShopTheLookContent
					hasOnlineSale={hasOnlineSale}
					products={products}
					look={look}
					onAddToCart={onAddToCart}
				/>
				<Snackbar
					delay={NOTIFICATION_DELAY}
					ref={snackbarRef}
					id='addItemNotification'
					title={t('snackbar.addToBag.confirmation.text')}
					icon={<IconDone color='content-inverse' />}
					position={SnackbarPosition.TOP}
				/>
			</Modal>
		</>
	) : null
}
